import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getLivePrice } from './helpers';
import {
  ZONE, findSkinImage, fmt, signed, pct, pnlColor, drop,
  ZoneHead, ZoneStats, ZoneSearch, ZoneSelect, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { archiveName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal, { mergeHistories } from './ItemDetailModal';
import { buildVault, getFolders, applyFolderPick, folderImage, UNFILED } from './investments';
import { useItemSets, planFileBySet, setById } from './collections';
import FolderPickModal from './FolderPickModal';
import FolderEditModal from './FolderEditModal';

// Investments Zone — the Vault on the Overview 7 board.
//
// Three levels, each one a drill-down of the one above:
//
//   folder     a pile you named ("Knives"), with its own cost/value/P-L
//   position   one skin in one condition inside that folder
//   unit       one physical item, with its float and pattern, that can be sold,
//              moved to another folder, or sent back to Live
//
// The Vault is now exclusive: anything in here is gone from the Live tabs, so a
// number on this page never double-counts one on that one. See investments.js
// for the rule and why it is kept in one place.
const SORTS = [
  { key: 'value', label: 'Biggest position' },
  { key: 'gain',  label: 'Best gain' },
  { key: 'loss',  label: 'Worst gain' },
  { key: 'age',   label: 'Held longest' },
  { key: 'qty',   label: 'Most units' },
];

const SORTERS = {
  value: (a, b) => b.value - a.value,
  gain:  (a, b) => (b.plPct ?? -Infinity) - (a.plPct ?? -Infinity),
  loss:  (a, b) => (a.plPct ?? Infinity) - (b.plPct ?? Infinity),
  age:   (a, b) => b.days - a.days,
  qty:   (a, b) => b.qty - a.qty,
};

const shortDate = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });

/** Float to six places — the precision the trading sites quote — or n/a. */
const floatText = (f) => (f == null ? 'n/a' : Number(f).toFixed(6));
const seedText  = (s) => (s == null ? 'n/a' : `#${s}`);

/**
 * How a position's units differ from each other, in one line.
 *
 * Ten Factory New copies of a skin are ten different items, and the float is
 * what tells them apart — so a position with more than one unit says the range
 * rather than picking one unit's float to show and implying the rest match.
 */
function floatSummary(units) {
  const floats = units.map(u => u.float).filter(f => f != null).map(Number);
  if (!floats.length) return null;
  if (units.length === 1 || floats.length === 1) return floatText(floats[0]);
  const lo = Math.min(...floats), hi = Math.max(...floats);
  if (lo === hi) return floatText(lo);
  return `${floatText(lo)} – ${floatText(hi)}`;
}

/* ─── One unit inside an expanded position ────────────────── */
function UnitRow({ unit, folders, onSell, onEdit, onAssignFolder, onReturnToLive, onMove }) {
  const { row, realIndex } = unit;
  return (
    <div className="zn-unit">
      <span className="zn-unit-cell dim">{shortDate(unit.bought)}</span>
      <span className="zn-unit-cell mono" title="Float value">{floatText(unit.float)}</span>
      <span className="zn-unit-cell mono" title="Paint seed / pattern index">{seedText(unit.paintSeed)}</span>
      <span className="zn-unit-cell">${fmt(unit.buy)}</span>
      <span className="zn-unit-cell" style={{ color: unit.pl == null ? ZONE.mute : pnlColor(unit.pl) }}>
        {unit.pl == null ? 'n/a' : signed(unit.pl)}
        {unit.plPct != null && <em className="zn-unit-pct">{pct(unit.plPct)}</em>}
      </span>
      <span className="zn-unit-acts">
        <button type="button" className="zn-mini sell" title="Record this exact unit as sold or traded"
          onClick={() => onSell?.(row, realIndex)}>Sell</button>
        {/* Folder is a property of the unit, so moving one is a one-click
            change of that property rather than a drag between two lists.
            "New folder…" hands off to the picker, the only thing that can
            make one. */}
        <select className="zn-mini-select" value={row.investmentFolder || ''}
          aria-label="Move this unit"
          onChange={e => {
            const v = e.target.value;
            if (v === '__live__') onReturnToLive?.([realIndex]);
            else if (v === '__new__') onMove?.();
            else onAssignFolder?.([realIndex], v || null);
          }}>
          <option value="">Unfiled</option>
          {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          <option value="__new__">+ New folder…</option>
          <option value="__live__">← Back to Live</option>
        </select>
        <button type="button" className="zn-mini" title="Edit this unit"
          onClick={() => onEdit?.(row, realIndex)}>Edit</button>
      </span>
    </div>
  );
}

/* ─── One position (a skin + condition inside a folder) ───── */
function PositionRow({
  p, i, img, maxAbsPct, move, folders, expanded, onToggle, onDetail,
  onSell, onEdit, onAssignFolder, onReturnToLive, onMove,
}) {
  const barW  = p.plPct != null ? Math.max(4, Math.abs(p.plPct) / maxAbsPct * 100) : 0;
  const spread = floatSummary(p.units);
  const seeds  = p.units.map(u => u.paintSeed).filter(s => s != null);

  return (
    <div className="zn-pos-wrap">
      <motion.div className="zn-pos" {...drop(i + 3, 0)}>

        {/* The unit count is the drill-down: ten units is ten different items,
            and this is where you go to see which. */}
        <button type="button" className="zn-pos-qty as-btn"
          style={{ background: p.pl == null ? ZONE.white : p.pl >= 0 ? ZONE.lime : ZONE.pink }}
          aria-expanded={expanded}
          title={expanded ? 'Hide the individual items' : 'Show each item, with its float and pattern'}
          onClick={onToggle}>
          <span className="n">{p.qty}</span>
          <span className="x">unit{p.qty !== 1 ? 's' : ''}</span>
        </button>

        {img
          ? <img src={img} alt="" />
          : <div className="zn-thumb-blank" style={{ width: 74, height: 50 }} />}

        <div className="zn-pos-main">
          <div className="zn-pos-name" title={p.skin}>{p.skin}</div>
          <div className="zn-pos-meta">
            <span>{p.cond || 'No condition'}</span>
            <span>held {p.days}d</span>
            <span>avg ${fmt(p.avg)}/unit</span>
            {/* Float and pattern are what identify one physical item, so they
                sit on the face of the position rather than behind a click. */}
            {spread && <span className="mono" title="Float value">float {spread}</span>}
            {seeds.length === 1 && p.qty === 1 && (
              <span className="mono" title="Paint seed / pattern index">pattern #{seeds[0]}</span>
            )}
            {move && (
              <span title={readingNote(move)} style={{ color: pnlColor(move.change) }}>
                market {pct(move.pct)} since · was ${fmt(move.then)}
              </span>
            )}
          </div>
          {p.plPct != null && (
            <div className="zn-meter">
              <motion.div className="fill"
                style={{ background: p.plPct >= 0 ? ZONE.green : ZONE.hot }}
                initial={{ width: 0 }}
                animate={{ width: `${barW}%` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 + Math.min(i, 14) * 0.04 }}
              />
            </div>
          )}
        </div>

        <div className="zn-pos-col">
          <div className="k">Cost</div>
          <div className="v">${fmt(p.cost)}</div>
        </div>
        <div className="zn-pos-col">
          <div className="k">Value</div>
          <div className="v">{p.price != null ? `$${fmt(p.value)}` : 'n/a'}</div>
        </div>
        <div className="zn-pos-col" style={{ minWidth: 104 }}>
          <div className="k">P/L</div>
          <div className="v" style={{ color: p.pl == null ? ZONE.mute : pnlColor(p.pl) }}>
            {p.pl == null ? 'n/a' : signed(p.pl)}
            {p.plPct != null && (
              <span style={{ fontSize: 14.5, display: 'block', fontFamily: "'Archivo', sans-serif", fontWeight: 800 }}>
                {pct(p.plPct)}
              </span>
            )}
          </div>
        </div>

        <div className="zn-pos-acts">
          <button type="button" className="zn-mini" onClick={onDetail}>Details</button>
          {/* Moves every unit in the position at once. A position never
              straddles two folders, so there is one answer for the whole
              group and no need to expand it first. */}
          <button type="button" className="zn-mini"
            title={`Move all ${p.qty} unit${p.qty !== 1 ? 's' : ''} to another folder`}
            onClick={() => onMove?.(p)}>Move</button>
          {/* A one-unit position has nothing to drill into, so it offers the
              sale directly instead of making you open a list of one. */}
          {p.qty === 1 && (
            <button type="button" className="zn-mini sell"
              onClick={() => onSell?.(p.units[0].row, p.units[0].realIndex)}>Sell</button>
          )}
        </div>
      </motion.div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div className="zn-units"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}>
            <div className="zn-unit head">
              <span>Bought</span><span>Float</span><span>Pattern</span>
              <span>Cost</span><span>P/L now</span><span />
            </div>
            {p.units.map(u => (
              <UnitRow key={u.row.id || u.realIndex} unit={u} folders={folders}
                onSell={onSell} onEdit={onEdit}
                onAssignFolder={onAssignFolder} onReturnToLive={onReturnToLive}
                onMove={() => onMove?.(p, u)} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function InvestmentsTabZone({
  rows, settings, skinsData = [], livePrices, csfloatPrices,
  onSell, onEdit, onCreateFolder, onUpdateFolder, onDeleteFolder,
  onAssignFolder, onReturnToLive,
}) {
  const [query, setQuery]   = useState('');
  const [sort, setSort]     = useState('value');
  const [detail, setDetail] = useState(null);   // the position whose modal is open
  const [openFolders, setOpenFolders] = useState(() => new Set());
  const [openPositions, setOpenPositions] = useState(() => new Set());
  // The folder editor, wrapped in an object so "closed" and "open on a new
  // folder" stay distinct: null is closed, { folder: null } is create.
  const [editing, setEditing] = useState(null);
  // The units the folder picker is open for: { indexes, from, label }.
  const [moving, setMoving] = useState(null);

  const folders = useMemo(() => getFolders(settings), [settings]);
  const itemSets = useItemSets();
  const vault = useMemo(
    () => buildVault({ rows, livePrices, settings, getPrice: getLivePrice }),
    [rows, livePrices, settings],
  );

  // Market drift since each position was opened, priced from the CSFloat
  // archive on the day the oldest unit was bought. A different question from the
  // P/L bar: that one asks what your entry was worth, this one asks what the
  // market did underneath it.
  const archivePoints = useMemo(() => vault.positions.map(p => ({
    key: p.key, name: archiveName(p.skin, p.cond), date: dayOf(new Date(p.oldest)),
  })), [vault.positions]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  // Search narrows the positions inside each folder; a folder that keeps nothing
  // drops out entirely rather than sitting there claiming totals for items the
  // search already rejected.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const by = SORTERS[sort];
    return vault.groups
      .map(g => ({
        ...g,
        positions: (q ? g.positions.filter(p => p.skin.toLowerCase().includes(q)) : [...g.positions]).sort(by),
      }))
      .filter(g => g.positions.length > 0 || (!q && !g.unfiled));
  }, [vault.groups, query, sort]);

  const shown = useMemo(
    () => groups.reduce((s, g) => s + g.positions.length, 0),
    [groups],
  );

  // One scale for every meter on the page, so two bars of the same length mean
  // the same thing whichever folder they are in.
  const maxAbsPct = useMemo(
    () => Math.max(1, ...vault.positions.map(p => Math.abs(p.plPct ?? 0))),
    [vault.positions],
  );

  // Vault-wide market drift, weighted by units held. Positions the archive
  // cannot price stay out of both sides rather than counting as flat.
  const drift = useMemo(() => vault.positions.reduce((acc, p) => {
    const mv = archive.get(p.key);
    if (!mv) return acc;
    return { change: acc.change + mv.change * p.qty, base: acc.base + mv.then * p.qty, n: acc.n + 1 };
  }, { change: 0, base: 0, n: 0 }), [vault.positions, archive]);
  const driftPct = drift.base > 0 ? (drift.change / drift.base) * 100 : null;

  const toggle = (setter) => (key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleFolder   = toggle(setOpenFolders);
  const togglePosition = toggle(setOpenPositions);

  // Creating and editing are the same modal, so a folder can be named, coloured
  // and given its picture in one pass instead of being made first and dressed
  // afterwards.
  const commitEdit = ({ name, color, image }) => {
    if (editing?.folder) onUpdateFolder?.(editing.folder.id, { name, color, image });
    else onCreateFolder?.(name, { color, image });
  };

  // One position, or one unit inside it when the unit's own select asked for a
  // folder that does not exist yet.
  const openMove = (p, unit = null) => {
    const units = unit ? [unit] : p.units;
    setMoving({
      indexes: units.map(u => u.realIndex),
      names: units.map(u => u.row.skin?.value).filter(Boolean),
      from: p.folderId,
      label: unit
        ? `${p.skin}${p.cond ? ` (${p.cond})` : ''}`
        : `${p.qty} × ${p.skin}${p.cond ? ` (${p.cond})` : ''}`,
    });
  };

  const commitMove = (pick) => {
    onAssignFolder?.(moving.indexes, applyFolderPick(pick, folders, onCreateFolder));
  };

  /**
   * File the whole unfiled pile by the collection, case or capsule each item
   * came from.
   *
   * Only the unfiled pile: anything already in a folder was put there on
   * purpose, and a bulk action that overrode that would be a worse trade than
   * leaving it alone. Knives and gloves resolve to no set and stay where they
   * are, which is what the summary counts.
   */
  const fileBySet = () => {
    const plan = planFileBySet(itemSets, vault.positions.filter(p => p.folderId === UNFILED));
    if (!plan.units) { window.alert(plan.message); return; }
    if (!window.confirm(plan.message)) return;
    for (const g of plan.groups) {
      onAssignFolder?.(g.indexes, applyFolderPick({ set: g.set }, folders, onCreateFolder));
    }
  };

  const removeFolder = (g) => {
    const msg = g.units > 0
      ? `Delete "${g.name}"? Its ${g.units} unit${g.units !== 1 ? 's' : ''} stay in the vault and become unfiled.`
      : `Delete "${g.name}"?`;
    if (window.confirm(msg)) onDeleteFolder?.(g.id);
  };

  const totals = vault.totals;

  if (!vault.positions.length && !folders.length) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="VAULT" sub="Long-term holds, kept apart from the flips." />
        <ZoneEmpty>
          The vault is empty. Move items here from the Armory and they leave the
          Live tab — an investment has one home, not two.
        </ZoneEmpty>
      </div>
    );
  }

  const roi = totals.cost > 0 ? (totals.pl / totals.cost) * 100 : null;

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="VAULT"
        sub="Everything held for the long run. Investments do not appear in Live — this is their only home. Open a folder to see its positions, then a position to see each item's float and pattern."
        stickers={[
          { text: `${totals.count} positions · ${totals.units} units`, bg: ZONE.teal },
          totals.oldestDays > 0 && { text: `oldest hold: ${totals.oldestDays}d`, bg: ZONE.white },
        ]}
      />

      <ZoneStats items={[
        { label: 'Capital in vault', value: `$${fmt(totals.cost)}`, sub: `across ${totals.units} units`, bg: ZONE.white },
        { label: 'Current value', value: `$${fmt(totals.value)}`,
          sub: totals.priced < totals.count
            ? `${totals.priced} of ${totals.count} positions priced`
            : 'at live market prices',
          bg: ZONE.yellow },
        { label: 'Vault P/L', value: signed(totals.pl),
          sub: roi != null ? `${pct(roi)} on cost` : 'n/a',
          bg: totals.pl >= 0 ? ZONE.lime : ZONE.hot },
        drift.n > 0 && {
          label: 'Market since you bought', value: signed(drift.change),
          sub: `${driftPct != null ? `${pct(driftPct)} · ` : ''}${drift.n} of ${totals.count} priced from history`,
          bg: drift.change >= 0 ? ZONE.lime : ZONE.pink },
      ].filter(Boolean)} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <ZoneSearch value={query} onChange={setQuery} placeholder="Search the vault…" />
        <button type="button" className="zn-btn primary" onClick={() => setEditing({ folder: null })}>+ New folder</button>
        {itemSets.sets.length > 0 && (
          <button type="button" className="zn-btn" onClick={fileBySet}
            title="Sort every unfiled unit into a folder named after the collection, case or capsule it came from">
            File by collection
          </button>
        )}
        <span className="zn-count" style={{ marginLeft: 'auto' }}>
          {shown} of {totals.count} positions
        </span>
        <ZoneSelect value={sort} onChange={setSort} options={SORTS} label="Sort positions" />
      </div>

      {/* ── Folders ────────────────────────────────── */}
      {groups.length === 0 ? (
        <ZoneEmpty>Nothing matches the search.</ZoneEmpty>
      ) : groups.map((g, gi) => {
        const open = openFolders.has(g.id);
        // A folder wears a picture instead of the colour chip: the one the user
        // picked in the editor, or the icon of the set it stands for. Either
        // says which folder at a glance, which the chip never could.
        const folderSet = setById(itemSets, g.setId);
        const img = folderImage(g, folderSet);
        return (
          <motion.div key={g.id} className={`zn-fold${open ? ' open' : ''}`} {...drop(gi + 2, 0)}>

            <div className="zn-fold-head">
              <button type="button" className="zn-fold-toggle" aria-expanded={open}
                onClick={() => toggleFolder(g.id)}
                title={open ? 'Collapse' : 'Expand'}>
                <span className="chev">{open ? '▾' : '▸'}</span>
                {img
                  ? <img className="zn-fold-img" src={img} alt="" loading="lazy" />
                  : <span className="swatch" style={{ background: g.color || ZONE.white }} />}
                <span className="zn-fold-name">{g.name}</span>
                <span className="zn-fold-count">
                  {g.positions.length} position{g.positions.length !== 1 ? 's' : ''} · {g.units} unit{g.units !== 1 ? 's' : ''}
                </span>
              </button>

              <div className="zn-fold-nums">
                <div className="zn-pos-col">
                  <div className="k">Cost</div>
                  <div className="v">${fmt(g.cost)}</div>
                </div>
                <div className="zn-pos-col">
                  <div className="k">Value</div>
                  <div className="v">{g.priced ? `$${fmt(g.value)}` : 'n/a'}</div>
                </div>
                <div className="zn-pos-col" style={{ minWidth: 104 }}>
                  <div className="k">P/L</div>
                  <div className="v" style={{ color: g.priced ? pnlColor(g.pl) : ZONE.mute }}>
                    {g.priced ? signed(g.pl) : 'n/a'}
                    {g.priced > 0 && g.plPct != null && (
                      <span style={{ fontSize: 14.5, display: 'block', fontFamily: "'Archivo', sans-serif", fontWeight: 800 }}>
                        {pct(g.plPct)}
                      </span>
                    )}
                  </div>
                </div>
                {/* The unfiled pile is not a folder the user made, so it cannot
                    be edited or deleted — only emptied by filing its units. */}
                {!g.unfiled && (
                  <div className="zn-fold-acts">
                    <button type="button" className="zn-mini"
                      title="Rename it, recolour it, or give it a picture"
                      onClick={() => setEditing({ folder: folders.find(f => f.id === g.id) || null })}>Edit</button>
                    <button type="button" className="zn-mini danger"
                      onClick={() => removeFolder(g)}>Delete</button>
                  </div>
                )}
              </div>
            </div>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div className="zn-fold-body"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}>
                  {g.positions.length === 0 ? (
                    <div className="zn-fold-blank">
                      Empty. Open a position elsewhere in the vault and move a unit in here.
                    </div>
                  ) : g.positions.map((p, i) => (
                    <PositionRow key={p.key} p={p} i={i}
                      img={findSkinImage(skinsData, p.skin, p.cond)}
                      maxAbsPct={maxAbsPct}
                      move={archive.get(p.key)}
                      folders={folders}
                      expanded={openPositions.has(p.key)}
                      onToggle={() => togglePosition(p.key)}
                      onDetail={() => setDetail({ ...p, img: findSkinImage(skinsData, p.skin, p.cond) })}
                      onSell={onSell} onEdit={onEdit}
                      onAssignFolder={onAssignFolder} onReturnToLive={onReturnToLive}
                      onMove={openMove} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}

      <ZoneFooter stickers={[
        { text: 'a unit belongs to one folder', bg: ZONE.purple },
        { text: `vault ${signed(totals.pl)}`, bg: totals.pl >= 0 ? ZONE.lime : ZONE.hot, right: true },
      ]} />

      {editing && (
        <FolderEditModal
          folder={editing.folder}
          folders={folders}
          skinsData={skinsData}
          variant="zone"
          onSave={commitEdit}
          onClose={() => setEditing(null)}
        />
      )}

      {moving && (
        <FolderPickModal
          folders={folders}
          initialId={moving.from}
          names={moving.names}
          variant="zone"
          title="Move to another folder"
          subtitle={moving.label}
          confirmLabel={`Move ${moving.indexes.length} unit${moving.indexes.length !== 1 ? 's' : ''}`}
          note="The items stay in the vault either way."
          onConfirm={commitMove}
          onClose={() => setMoving(null)}
        />
      )}

      {detail && (
        <ItemDetailModal
          skinsData={skinsData}
          variant="zone"
          kind="position"
          name={detail.skin}
          condition={detail.cond}
          image={detail.img}
          history={mergeHistories(detail.units.map(u => u.row.priceHistory || []))}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          qty={detail.qty}
          cost={detail.cost}
          avg={detail.avg}
          days={detail.days}
          units={detail.units.map(u => u.row)}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
